import { v4 as uuidv4 } from "uuid";
import {
  doc,
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  limit
} from "firebase/firestore";

import { db, uploadFileToStorage } from "./firebase";
import { fetchOurApi } from "./utils/apiCalls";

const BUCKET = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;

// A ZIP's contents are unpacked server-side (PRD §8.1) -- the front-end
// uploads the raw ZIP as one Storage object and lists it as one pre-unpack
// entry with isZip: true. Everything else uploads as a standalone file.
//
// `fileIndex` (this file's position in the user's selection) is part of the
// storage path so two files that sanitize to the same name never collide --
// `file.name` is only the BASENAME even for a folder/webkitdirectory
// selection (e.g. "2024/utility.pdf" and "2025/utility.pdf" both arrive as
// "utility.pdf"), so name-only paths would silently overwrite one file with
// another's bytes. Matches the same uniquification the server applies to
// ZIP members (smartUploadTasks.js's `${Date.now()}-${memberIndex}-...`).
const uploadOneSmartUploadFile = async ({
  companyId,
  batchId,
  file,
  fileIndex
}) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${companyId}/smart-uploads/${batchId}/${fileIndex}-${safeName}`;
  await uploadFileToStorage({
    path,
    fileObject: file,
    hasUserConfirmedUpload: true
  });
  return {
    fileName: file.name,
    gcsPath: path,
    mimeType: file.type || null,
    isZip: safeName.toLowerCase().endsWith(".zip")
  };
};

// Uploads every file via Promise.allSettled -- one failed Storage upload
// becomes one rejected-file report rather than aborting the whole batch
// (design-intent 3.9, "one bad file never takes down its batch"). This is
// the one place this flow deliberately diverges from bomBulkUpload.js's
// Promise.all, which fails the entire batch on a single upload error.
export const uploadSmartUploadFiles = async ({ companyId, batchId, files }) => {
  const settled = await Promise.allSettled(
    files.map((file, fileIndex) =>
      uploadOneSmartUploadFile({ companyId, batchId, file, fileIndex })
    )
  );

  const uploaded = settled
    .filter(({ status }) => status === "fulfilled")
    .map(({ value }) => value);
  const failed = settled
    .map((result, index) => ({ result, file: files[index] }))
    .filter(({ result }) => result.status === "rejected")
    .map(({ file, result }) => ({
      fileName: file.name,
      reason: result.reason?.message || "Upload failed"
    }));

  return { uploaded, failed };
};

export const startSmartUploadBatch = async ({
  companyId,
  files,
  entityContext = null
}) => {
  if (!files?.length) {
    return { error: "no-files" };
  }
  const batchId = uuidv4();
  const { uploaded, failed } = await uploadSmartUploadFiles({
    companyId,
    batchId,
    files
  });

  if (!uploaded.length) {
    return { error: "all-uploads-failed", failed };
  }

  const result = await fetchOurApi({
    accountId: companyId,
    path: "/smart-upload/start-batch",
    method: "POST",
    data: {
      batchId,
      bucket: BUCKET,
      files: uploaded,
      entityContext
    }
  });

  if (!result?.taskDocId) {
    return { error: result?.message || "task-not-created", failed };
  }
  return { taskDocId: result.taskDocId, batchId, failed };
};

export const subscribeToSmartUploadTask = ({
  companyId,
  taskDocId,
  onUpdate
}) => {
  const ref = doc(db, "v3-companies", companyId, "tasks", taskDocId);
  return onSnapshot(ref, (snapshot) => {
    if (!snapshot.exists()) {
      return;
    }
    onUpdate(snapshot.data());
  });
};

export const subscribeToSmartUploadTaskData = ({
  companyId,
  taskDocId,
  onUpdate
}) => {
  const ref = collection(
    db,
    "v3-companies",
    companyId,
    "tasks",
    taskDocId,
    "taskData"
  );
  return onSnapshot(ref, (snapshot) => {
    onUpdate(
      snapshot.docs.map((rowDoc) => ({ id: rowDoc.id, ...rowDoc.data() }))
    );
  });
};

export const subscribeToSmartUploadLines = ({
  companyId,
  taskDocId,
  rowId,
  onUpdate
}) => {
  const ref = collection(
    db,
    "v3-companies",
    companyId,
    "tasks",
    taskDocId,
    "taskData",
    rowId,
    "lines"
  );
  return onSnapshot(ref, (snapshot) => {
    onUpdate(
      snapshot.docs.map((lineDoc) => ({ id: lineDoc.id, ...lineDoc.data() }))
    );
  });
};

const postSmartUploadAction = ({ companyId, action, data }) =>
  fetchOurApi({
    accountId: companyId,
    path: `/smart-upload/${action}`,
    method: "POST",
    data
  });

// Line docs are server-write-only (firestore.rules), so every review edit --
// values, acknowledgements, allocation, reject/undo -- goes through the API.
export const updateSmartUploadLines = ({
  companyId,
  taskDocId,
  rowId,
  updates
}) =>
  postSmartUploadAction({
    companyId,
    action: "update-lines",
    data: { taskDocId, rowId, updates }
  });

export const commitSmartUploadLines = ({
  companyId,
  taskDocId,
  lines,
  confirmSlideoutPrompt = false
}) =>
  postSmartUploadAction({
    companyId,
    action: "commit-lines",
    data: {
      taskDocId,
      lines,
      ...(confirmSlideoutPrompt ? { confirmSlideoutPrompt: true } : {})
    }
  });

// #42's "Continue with guided manual entry" flow. clientEntryId is the
// caller's -- the dialog mints it once on open (uuidv4) and keeps sending
// that same value across a refused save, a retried request after a dropped
// response, or even a double-click of Save, so every one of those replays
// onto the same line (`M-{clientEntryId}`) instead of creating a second one.
// Minting it inside this helper would hand back a fresh id on every call,
// defeating that idempotency -- the caller only gets a new one after a
// committed save. replacesLineId carries the id of a line being superseded
// (the per-line "Enter manually" hand-off), omitted for a fresh row-level
// entry.
export const appendManualSmartUploadLine = ({
  companyId,
  taskDocId,
  rowId,
  clientEntryId,
  manualLine,
  replacesLineId,
  confirmSlideoutPrompt = false
}) =>
  postSmartUploadAction({
    companyId,
    action: "append-manual-line",
    data: {
      taskDocId,
      rowId,
      clientEntryId,
      manualLine,
      ...(replacesLineId ? { replacesLineId } : {}),
      ...(confirmSlideoutPrompt ? { confirmSlideoutPrompt: true } : {})
    }
  });

// Ticket #389's snip-and-comment capture: captureSnippet (@aclymatepackages/
// annotations) hands back a data: URL, which Storage can't take directly --
// fetch()/blob() is the standard browser-native way to turn one into a Blob.
// Same path convention as uploadOneSmartUploadFile's own smart-uploads/ tree.
export const uploadSmartUploadAnnotationThumbnail = async ({
  companyId,
  taskDocId,
  rowId,
  clientEntryId,
  dataUrl
}) => {
  const blob = await (await fetch(dataUrl)).blob();
  const path = `${companyId}/smart-uploads/${taskDocId}/annotations/${rowId}-${clientEntryId}.png`;
  await uploadFileToStorage({ path, fileObject: blob, hasUserConfirmedUpload: true });
  return path;
};

export const retrySmartUploadDocumentFiling = ({
  companyId,
  taskDocId,
  rowId,
  lineId
}) =>
  postSmartUploadAction({
    companyId,
    action: "retry-document-filing",
    data: { taskDocId, rowId, lineId }
  });

export const dismissSmartUploadRow = ({ companyId, taskDocId, rowId }) =>
  postSmartUploadAction({
    companyId,
    action: "dismiss-row",
    data: { taskDocId, rowId }
  });

// Migrated CSV review (ticket #393). A transactions or shipping CSV can hold
// thousands of lines, so the review reads counts, attention groups and one
// page of lines at a time from the server -- never a listener per row.
export const fetchCsvReviewSummary = ({ companyId, taskDocId }) =>
  postSmartUploadAction({
    companyId,
    action: "csv-review-summary",
    data: { taskDocId }
  });

export const fetchCsvAttentionGroups = ({ companyId, taskDocId }) =>
  postSmartUploadAction({
    companyId,
    action: "csv-attention-groups",
    data: { taskDocId }
  });

export const listCsvLines = ({
  companyId,
  taskDocId,
  filter,
  groupKey,
  cursor = null,
  pageSize = 50
}) =>
  postSmartUploadAction({
    companyId,
    action: "csv-list-lines",
    data: {
      taskDocId,
      filter,
      cursor,
      limit: pageSize,
      ...(groupKey ? { groupKey } : {})
    }
  });

export const updateCsvLineGroup = ({
  companyId,
  taskDocId,
  groupKey,
  fields
}) =>
  postSmartUploadAction({
    companyId,
    action: "csv-update-line-group",
    data: { taskDocId, groupKey, fields }
  });

export const startCsvBulkCommit = ({
  companyId,
  taskDocId,
  expectedReadyCount,
  confirmSlideoutPrompt = false
}) =>
  postSmartUploadAction({
    companyId,
    action: "csv-start-bulk-commit",
    data: {
      taskDocId,
      expectedReadyCount,
      ...(confirmSlideoutPrompt ? { confirmSlideoutPrompt: true } : {})
    }
  });

export const cancelCsvBulkCommit = ({ companyId, taskDocId, runId }) =>
  postSmartUploadAction({
    companyId,
    action: "csv-cancel-bulk-commit",
    data: { taskDocId, runId }
  });

// Rows and the task are server-write-only for these families
// (firestore.rules), so "fix a cell and retry" goes through the API.
export const retryErroredCsvRows = ({ companyId, taskDocId, edits = [] }) =>
  postSmartUploadAction({
    companyId,
    action: "csv-retry-errored-rows",
    data: { taskDocId, edits }
  });

// A missing run doc is reported through onMissing rather than ignored: the
// run id came from the task or the start response, so the doc should exist.
export const subscribeToBulkCommitRun = ({
  companyId,
  taskDocId,
  runId,
  onUpdate,
  onMissing,
  onError
}) => {
  const ref = doc(
    db,
    "v3-companies",
    companyId,
    "tasks",
    taskDocId,
    "bulkCommits",
    runId
  );
  return onSnapshot(
    ref,
    (snapshot) => {
      if (!snapshot.exists()) {
        return onMissing();
      }
      return onUpdate({ id: snapshot.id, ...snapshot.data() });
    },
    onError
  );
};

export const ERRORED_CSV_ROWS_LIMIT = 100;

export const fetchErroredCsvRows = async ({ companyId, taskDocId }) => {
  const snapshot = await getDocs(
    query(
      collection(db, "v3-companies", companyId, "tasks", taskDocId, "taskData"),
      where("status", "==", "error"),
      limit(ERRORED_CSV_ROWS_LIMIT)
    )
  );
  return snapshot.docs.map((rowDoc) => ({ id: rowDoc.id, ...rowDoc.data() }));
};

const NON_CSV_ROW_FIELDS = [
  "id",
  "taskNum",
  "document_family",
  "rawAmount",
  "status",
  "error",
  "errorMessage",
  "reason",
  "isEdited",
  "ignoreInReprocess",
  "extract_started_at",
  "extract_attempts",
  "extract_attempt_id",
  "updated_at"
];

// Account Activity's reprocess for a migrated CSV: only the CSV cells a
// person changed are sent (the server refuses any other column), and an
// ignored row is sent as ignoreInReprocess alone.
export const buildCsvRetryEdits = ({ displayedRows, originalRows }) =>
  displayedRows
    .filter(({ isEdited, ignoreInReprocess }) => isEdited || ignoreInReprocess)
    .map((row) => {
      if (row.ignoreInReprocess) {
        return { rowId: row.id, ignoreInReprocess: true };
      }
      const original = originalRows.find(({ id }) => id === row.id) || {};
      const fields = Object.fromEntries(
        Object.keys(original)
          .filter((field) => !NON_CSV_ROW_FIELDS.includes(field))
          .filter((field) => row[field] !== original[field])
          .map((field) => [field, row[field]])
      );
      return { rowId: row.id, fields };
    });

// Smart Upload files documents on the entity its lines were committed to; they
// back live activity records, so both Documentation surfaces show them
// read-only (firestore.rules refuses the update/delete too). Archiving a batch
// archives its filings, which are then hidden -- filtered client-side because
// ordinary documentation docs predate the archived field.
export const isSmartUploadDocument = ({ source } = {}) =>
  source === "smart_upload";

export const isVisibleDocumentationFile = (file) =>
  !(isSmartUploadDocument(file) && file.archived === true);
